import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, QueryView } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { Modal } from '../../ui/Modal';
import { ActorName, Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import type { AttemptDetail } from './types';

const PROVENANCE_LABEL: Record<string, string> = {
  reported: 'Reported',
  verified: 'Verified',
  webhook: 'From GitHub',
};

/** WF-12–16: immutable binding, executor report, provenance, reconciliation, verification and corrections. */
export function AttemptPage({ rollback }: { rollback?: boolean }) {
  const { key, releaseId, rollbackId, attemptId } = useParams();
  const base = rollback
    ? `/projects/${key}/releases/${releaseId}/rollbacks/${rollbackId}/attempts/${attemptId}`
    : `/projects/${key}/releases/${releaseId}/attempts/${attemptId}`;
  const q = useQuery({ queryKey: ['attempt', base], queryFn: () => api.get<AttemptDetail>(base) });
  usePageTitle(
    q.data
      ? rollback
        ? `Rollback ${rollbackId} · Attempt ${q.data.attempt}`
        : `Release ${releaseId} · Deployment attempt ${q.data.attempt}`
      : null,
  );
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: `Release ${releaseId}`, to: `/projects/${key}/releases/${releaseId}` },
    ...(rollback
      ? [
          {
            label: `Rollback ${rollbackId}`,
            to: `/projects/${key}/releases/${releaseId}/rollbacks/${rollbackId}`,
          },
        ]
      : []),
    { label: attemptId ?? '' },
  ]);
  const [dialog, setDialog] = useState<'report' | 'reconcile' | 'verify' | 'correct' | null>(null);
  const [checkDialog, setCheckDialog] = useState<string | null>(null);
  const inv = [['attempt', base], ['release', key, releaseId], ['releases', key], ['ticket']];
  return (
    <div className="page">
      <QueryView query={q}>
        {(a) => {
          const b = a.binding;
          return (
            <>
              <PageHeader
                title={rollback ? `Rollback attempt ${a.attempt}` : `Deployment attempt ${a.attempt}`}
                subtitle={
                  <>
                    {a.release.displayId} · {a.release.title} → {b.environmentName}
                  </>
                }
                actions={
                  <>
                    <StateBadge state={a.state} />
                    {a.provenance && (
                      <StateBadge
                        state={a.provenance}
                        label={PROVENANCE_LABEL[a.provenance] ?? a.provenance}
                      />
                    )}
                    {a.viewer.canReport && (
                      <Button size="sm" onClick={() => setDialog('report')}>
                        Report outcome
                      </Button>
                    )}
                    {a.viewer.canReconcile && (
                      <Button size="sm" variant="secondary" onClick={() => setDialog('reconcile')}>
                        Reconcile
                      </Button>
                    )}
                    {a.viewer.canVerify && (
                      <Button size="sm" variant="secondary" onClick={() => setDialog('verify')}>
                        Verify
                      </Button>
                    )}
                    {a.viewer.canCorrect && (
                      <Button size="sm" variant="ghost" onClick={() => setDialog('correct')}>
                        Record correction
                      </Button>
                    )}
                  </>
                }
              />
              {a.awaitingVerification && (
                <Alert tone="info" title="Awaiting verification">
                  The Deploy run succeeded. An admin records the manual checks below; the attempt succeeds
                  when they all pass, or fails when one fails.
                </Alert>
              )}
              {a.state === 'deploying' && !a.awaitingVerification && (
                <Alert tone="info" title="Executing externally">
                  The executor deploys outside this application and reports here. Overlapping attempts in{' '}
                  {b.environmentName} are blocked.
                </Alert>
              )}
              {a.state === 'unknown' && !a.awaitingVerification && (
                <Alert tone="warning" title="Outcome unknown">
                  No result was reported
                  {a.unknownAt ? (
                    <>
                      {' '}
                      (marked unknown <Timestamp value={a.unknownAt} />)
                    </>
                  ) : (
                    ''
                  )}
                  . Timeout never infers success or failure. The executor or an admin must reconcile with
                  evidence before the environment unlocks.
                </Alert>
              )}
              {(a.provenance === 'reported' || a.provenance === 'webhook') && a.state === 'succeeded' && (
                <Alert tone="info">
                  This result was reported by {a.report?.reportedBy.name}; the application did not
                  independently observe the deployment.
                </Alert>
              )}
              <div className="split">
                <div className="stack">
                  {a.run && (
                    <Card
                      title="Deploy run"
                      subtitle={
                        <>
                          Reported by GitHub <Timestamp value={a.run.receivedAt} />
                        </>
                      }
                    >
                      <KeyValue
                        items={[
                          ['Conclusion', <StateBadge key="c" state={a.run.conclusion} />],
                          [
                            'Run',
                            <a key="u" href={a.run.url} target="_blank" rel="noopener noreferrer nofollow">
                              {a.run.url}
                            </a>,
                          ],
                          ['Started', <Timestamp key="s" value={a.run.startedAt} />],
                          ['Finished', <Timestamp key="f" value={a.run.finishedAt} />],
                        ]}
                      />
                      {a.awaitingVerification && (
                        <>
                          <span className="small strong">Verification checks</span>
                          <ul className="checklist">
                            {b.verificationChecks.map((c) => {
                              const r = a.checks.find((x) => x.name === c);
                              const manual = a.manualChecks.includes(c);
                              return (
                                <li key={c}>
                                  {r ? (
                                    <Badge tone={r.passed ? 'success' : 'error'}>
                                      {r.passed ? 'Passed' : 'Failed'}
                                    </Badge>
                                  ) : (
                                    <Badge tone="warning">No result</Badge>
                                  )}{' '}
                                  {c} <span className="small muted">({manual ? 'manual' : 'automatic'})</span>
                                  {r?.observation && <span className="small muted"> — {r.observation}</span>}
                                  {manual && a.viewer.canRecordChecks && (
                                    <>
                                      {' '}
                                      <Button size="sm" variant="ghost" onClick={() => setCheckDialog(c)}>
                                        {r ? 'Change result' : 'Record result'}
                                      </Button>
                                    </>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </>
                      )}
                    </Card>
                  )}
                  <Card
                    title="Reported result"
                    subtitle={
                      a.report ? (
                        <>
                          Received <Timestamp value={a.report.reportedAt} /> from{' '}
                          <ActorName actor={a.report.reportedBy} />
                        </>
                      ) : (
                        'No report yet.'
                      )
                    }
                  >
                    {a.report && (
                      <>
                        <KeyValue
                          items={[
                            ['Outcome', <StateBadge key="o" state={a.state} />],
                            [
                              'Revision',
                              <span key="r" className="mono">
                                {a.report.revision ?? '—'}
                              </span>,
                            ],
                            [
                              'Execution reference',
                              a.report.deploymentReference ? (
                                <a
                                  href={a.report.deploymentReference}
                                  target="_blank"
                                  rel="noopener noreferrer nofollow"
                                >
                                  {a.report.deploymentReference}
                                </a>
                              ) : (
                                '—'
                              ),
                            ],
                            ['Started (supplied)', <Timestamp key="s" value={a.report.startedAt} />],
                            ['Finished (supplied)', <Timestamp key="f" value={a.report.finishedAt} />],
                          ]}
                        />
                        <span className="small strong">Verification checks</span>
                        <ul className="checklist">
                          {b.verificationChecks.map((c) => {
                            const r = a.report!.checks.find((x) => x.name === c);
                            return (
                              <li key={c}>
                                {r ? (
                                  <Badge tone={r.passed ? 'success' : 'error'}>
                                    {r.passed ? 'Passed' : 'Failed'}
                                  </Badge>
                                ) : (
                                  <Badge tone="warning">No result</Badge>
                                )}{' '}
                                {c}
                                {a.manualChecks.includes(c) && <span className="small muted"> (manual)</span>}
                                {r?.observation && <span className="small muted"> — {r.observation}</span>}
                              </li>
                            );
                          })}
                        </ul>
                        {a.report.problems.length > 0 && (
                          <Alert tone="warning" title="Problems recorded">
                            <ul>
                              {a.report.problems.map((p, i) => (
                                <li key={i}>{p.message}</li>
                              ))}
                            </ul>
                          </Alert>
                        )}
                        {a.report.partialEffects && (
                          <>
                            <span className="small strong">Partial effects</span>
                            <Markdown>{a.report.partialEffects}</Markdown>
                          </>
                        )}
                        {a.report.errors && (
                          <>
                            <span className="small strong">Errors</span>
                            <Markdown>{a.report.errors}</Markdown>
                          </>
                        )}
                        {a.report.recoveryActions && (
                          <>
                            <span className="small strong">Remaining recovery actions</span>
                            <Markdown>{a.report.recoveryActions}</Markdown>
                          </>
                        )}
                        {a.report.log && (
                          <>
                            <span className="small strong">Log</span>
                            <pre className="code" style={{ maxHeight: 320, overflow: 'auto' }}>
                              {a.report.log}
                            </pre>
                          </>
                        )}
                      </>
                    )}
                  </Card>
                  {(a.closedTickets.length > 0 || a.reopenedTickets.length > 0) && (
                    <Card title={a.closedTickets.length ? 'Closed as done' : 'Reopened to Pending'}>
                      <ul className="checklist">
                        {[...a.closedTickets, ...a.reopenedTickets].map((t) => (
                          <li key={t.key}>
                            <Link to={`/projects/${a.project.key}/tickets/${t.number}`} className="mono">
                              {t.key}
                            </Link>{' '}
                            {t.title}
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}
                  {(a.verifications.length > 0 || a.corrections.length > 0) && (
                    <Card title="Admin decisions">
                      <ul className="checklist">
                        {a.verifications.map((v) => (
                          <li key={v.id} style={{ flexDirection: 'column', gap: 2 }}>
                            <span>
                              <Badge tone="success">Verified</Badge> by <ActorName actor={v.admin} /> ·{' '}
                              <Timestamp value={v.createdAt} />
                            </span>
                            <span className="small">
                              {v.reason} — {v.evidence}
                            </span>
                          </li>
                        ))}
                        {a.corrections.map((c) => (
                          <li key={c.id} style={{ flexDirection: 'column', gap: 2 }}>
                            <span>
                              <Badge tone="warning">
                                {c.kind === 'reconciliation' ? 'Reconciliation' : 'Correction'}
                              </Badge>{' '}
                              {c.previousOutcome} → {c.correctedOutcome} by <ActorName actor={c.admin} /> ·{' '}
                              <Timestamp value={c.createdAt} />
                            </span>
                            <span className="small">
                              {c.reason} — {c.evidence}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}
                </div>
                <div className="stack">
                  <Card title="Approved binding (immutable)" tight>
                    <KeyValue
                      items={[
                        ['Environment', b.environmentName],
                        [
                          'Revision',
                          <span key="r" className="mono small">
                            {b.sourceRevision ?? b.rollbackRevision}
                          </span>,
                        ],
                        [
                          'Artifact',
                          b.artifactDigest ? <span className="mono small">{b.artifactDigest}</span> : '—',
                        ],
                        ['Executor', <ActorName key="e" actor={a.executor} />],
                        [
                          'Approval',
                          a.approval ? (
                            <Link to={`/approvals/${a.approval.displayId}`}>{a.approval.displayId}</Link>
                          ) : (
                            '—'
                          ),
                        ],
                        [
                          'Approved by',
                          a.approval?.decidedBy ? <ActorName actor={a.approval.decidedBy} /> : '—',
                        ],
                        ['Registered', <Timestamp key="g" value={a.registeredAt} />],
                        ['Last progress', <Timestamp key="p" value={a.lastProgressAt} />],
                      ]}
                    />
                  </Card>
                  {b.tickets && (
                    <Card title="Tickets" tight>
                      <span className="small mono">{b.tickets.map((t) => t.key).join(', ')}</span>
                    </Card>
                  )}
                  {b.affectedTickets && (
                    <Card title="Affected tickets" tight>
                      <span className="small mono">
                        {b.affectedTickets.map((t) => t.key).join(', ') || 'None'}
                      </span>
                    </Card>
                  )}
                </div>
              </div>
              {dialog === 'report' && (
                <ReportDialog a={a} base={base} onClose={() => setDialog(null)} inv={inv} />
              )}
              {dialog === 'reconcile' && (
                <ReconcileDialog a={a} base={base} onClose={() => setDialog(null)} inv={inv} />
              )}
              {(dialog === 'verify' || dialog === 'correct') && (
                <AdminNoteDialog kind={dialog} base={base} onClose={() => setDialog(null)} inv={inv} />
              )}
              {checkDialog && (
                <ManualCheckDialog
                  a={a}
                  name={checkDialog}
                  base={base}
                  onClose={() => setCheckDialog(null)}
                  inv={inv}
                />
              )}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

function ChecksEditor({
  checks,
  value,
  onChange,
}: {
  checks: string[];
  value: Record<string, { passed: boolean; observation: string }>;
  onChange: (v: Record<string, { passed: boolean; observation: string }>) => void;
}) {
  return (
    <div className="stack sm">
      {checks.map((c) => (
        <div key={c} className="grid-2">
          <Field label={c}>
            {(p) => (
              <Select
                {...p}
                value={value[c]?.passed === undefined ? '' : String(value[c]!.passed)}
                onChange={(e) =>
                  onChange({
                    ...value,
                    [c]: { passed: e.target.value === 'true', observation: value[c]?.observation ?? '' },
                  })
                }
              >
                <option value="">No result</option>
                <option value="true">Passed</option>
                <option value="false">Failed</option>
              </Select>
            )}
          </Field>
          <Field label="Observation">
            {(p) => (
              <Input
                {...p}
                value={value[c]?.observation ?? ''}
                onChange={(e) =>
                  onChange({
                    ...value,
                    [c]: { passed: value[c]?.passed ?? false, observation: e.target.value },
                  })
                }
              />
            )}
          </Field>
        </div>
      ))}
    </div>
  );
}

function ReportDialog({
  a,
  base,
  onClose,
  inv,
}: {
  a: AttemptDetail;
  base: string;
  onClose: () => void;
  inv: unknown[][];
}) {
  const b = a.binding;
  const [outcome, setOutcome] = useState<'success' | 'failure' | 'unknown'>('success');
  const [ref, setRef] = useState('');
  const [rev, setRev] = useState(b.sourceRevision ?? b.rollbackRevision ?? '');
  const [checks, setChecks] = useState<Record<string, { passed: boolean; observation: string }>>({});
  const [partial, setPartial] = useState('');
  const [errors, setErrors] = useState('');
  const m = useAction(
    () =>
      api.post(`${base}/report`, {
        expectedVersion: a.version,
        sourceRevision: rev,
        artifactDigest: b.artifactDigest,
        environmentId: b.environmentId,
        outcome,
        deploymentReference: ref || null,
        checks: Object.entries(checks).map(([name, r]) => ({ name, ...r })),
        partialEffects: partial || null,
        errors: errors || null,
      }),
    { success: 'Outcome recorded', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title={`Report ${a.attempt}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate(undefined)} loading={m.isPending}>
            Record outcome
          </Button>
        </>
      }
    >
      <Field label="Outcome">
        {(p) => (
          <Select {...p} value={outcome} onChange={(e) => setOutcome(e.target.value as never)}>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="unknown">Unknown</option>
          </Select>
        )}
      </Field>
      <Field
        label="Deployed revision"
        hint="Must match the approved binding. If something else was deployed, report Unknown and describe it."
      >
        {(p) => <Input {...p} className="mono" value={rev} onChange={(e) => setRev(e.target.value)} />}
      </Field>
      <Field
        label="Execution reference or log link"
        hint="Required for success. A push or merge alone is not delivery."
      >
        {(p) => <Input {...p} value={ref} onChange={(e) => setRef(e.target.value)} />}
      </Field>
      <ChecksEditor checks={b.verificationChecks} value={checks} onChange={setChecks} />
      <Field label="Partial external effects" optional>
        {(p) => <Textarea {...p} rows={2} value={partial} onChange={(e) => setPartial(e.target.value)} />}
      </Field>
      <Field label="Errors" optional>
        {(p) => <Textarea {...p} rows={2} value={errors} onChange={(e) => setErrors(e.target.value)} />}
      </Field>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}

function ReconcileDialog({
  a,
  base,
  onClose,
  inv,
}: {
  a: AttemptDetail;
  base: string;
  onClose: () => void;
  inv: unknown[][];
}) {
  const [outcome, setOutcome] = useState<'success' | 'failure'>('failure');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [checks, setChecks] = useState<Record<string, { passed: boolean; observation: string }>>({});
  const m = useAction(
    () =>
      api.post(`${base}/reconcile`, {
        outcome,
        reason,
        evidence,
        checks: Object.entries(checks).map(([name, r]) => ({ name, ...r })),
      }),
    { success: 'Attempt reconciled', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title={`Reconcile ${a.attempt}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => m.mutate(undefined)}
            loading={m.isPending}
            disabled={!reason.trim() || !evidence.trim()}
          >
            Record reconciliation
          </Button>
        </>
      }
    >
      <Alert tone="info">
        Record what you verified in the real environment. Success requires passing results for every required
        check and closes eligible tickets with verified provenance.
      </Alert>
      <Field label="Observed outcome">
        {(p) => (
          <Select {...p} value={outcome} onChange={(e) => setOutcome(e.target.value as never)}>
            <option value="failure">Failure</option>
            <option value="success">Success</option>
          </Select>
        )}
      </Field>
      {outcome === 'success' && (
        <ChecksEditor checks={a.binding.verificationChecks} value={checks} onChange={setChecks} />
      )}
      <Field label="Evidence">
        {(p) => <Textarea {...p} rows={3} value={evidence} onChange={(e) => setEvidence(e.target.value)} />}
      </Field>
      <Field label="Reason">
        {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}

function AdminNoteDialog({
  kind,
  base,
  onClose,
  inv,
}: {
  kind: 'verify' | 'correct';
  base: string;
  onClose: () => void;
  inv: unknown[][];
}) {
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [corrected, setCorrected] = useState<'success' | 'failure' | 'unknown'>('failure');
  const m = useAction(
    () =>
      api.post(
        `${base}/${kind}`,
        kind === 'verify' ? { reason, evidence } : { reason, evidence, correctedOutcome: corrected },
      ),
    {
      success: kind === 'verify' ? 'Delivery verified' : 'Correction recorded',
      invalidate: inv as never,
      onSuccess: onClose,
    },
  );
  return (
    <Modal
      title={kind === 'verify' ? 'Verify reported delivery' : 'Record correction'}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => m.mutate(undefined)}
            loading={m.isPending}
            disabled={!reason.trim() || !evidence.trim()}
          >
            Save
          </Button>
        </>
      }
    >
      <p className="muted">
        {kind === 'verify'
          ? 'Your verification is recorded separately; the original report stays visible.'
          : 'Corrections are appended; the original report is never overwritten. Delivered tickets are not reopened automatically.'}
      </p>
      {kind === 'correct' && (
        <Field label="Corrected outcome">
          {(p) => (
            <Select {...p} value={corrected} onChange={(e) => setCorrected(e.target.value as never)}>
              <option value="failure">Failure</option>
              <option value="unknown">Unknown</option>
              <option value="success">Success</option>
            </Select>
          )}
        </Field>
      )}
      <Field label="Evidence">
        {(p) => <Textarea {...p} rows={3} value={evidence} onChange={(e) => setEvidence(e.target.value)} />}
      </Field>
      <Field label="Reason">
        {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}

/** An admin's result for one manual check after the Deploy run succeeded. */
function ManualCheckDialog({
  a,
  name,
  base,
  onClose,
  inv,
}: {
  a: AttemptDetail;
  name: string;
  base: string;
  onClose: () => void;
  inv: unknown[][];
}) {
  const current = a.checks.find((c) => c.name === name);
  const [passed, setPassed] = useState(current?.passed ?? true);
  const [observation, setObservation] = useState(current?.observation ?? '');
  const remaining = a.binding.verificationChecks.filter(
    (c) => c !== name && !a.checks.some((r) => r.name === c),
  ).length;
  const m = useAction(
    () => api.post(`${base}/checks`, { expectedVersion: a.version, name, passed, observation }),
    { success: 'Check recorded', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title="Record manual check"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate(undefined)} loading={m.isPending} disabled={!observation.trim()}>
            Save
          </Button>
        </>
      }
    >
      <p>{name}</p>
      <Field label="Result">
        {(p) => (
          <Select {...p} value={String(passed)} onChange={(e) => setPassed(e.target.value === 'true')}>
            <option value="true">Passed</option>
            <option value="false">Failed</option>
          </Select>
        )}
      </Field>
      <Field label="What you observed">
        {(p) => (
          <Textarea {...p} rows={3} value={observation} onChange={(e) => setObservation(e.target.value)} />
        )}
      </Field>
      {remaining === 0 && (
        <Alert tone="info">
          This is the last missing check: saving finishes {a.attempt} as{' '}
          {passed && a.checks.every((c) => c.name === name || c.passed) ? 'succeeded' : 'failed'}.
        </Alert>
      )}
      <ErrorAlert error={m.error} />
    </Modal>
  );
}
